import { useEffect, useRef } from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';

import type { EnterpriseProgramsV1 } from 'bundles/naptimejs/resources/__generated__/EnterpriseProgramsV1';
import type ThirdPartyOrganizationsV1 from 'bundles/naptimejs/resources/thirdPartyOrganizations.v1';

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

function omit<T extends object, K extends (keyof T)[]>(obj: T, keysToOmit: K): Omit<T, K[number]> {
  const result = { ...obj };

  for (const key of keysToOmit) {
    if (key in result) {
      delete result[key];
    }
  }

  return result;
}

type SsoCheckProps = {
  router: InjectedRouter;
  userId: number;
  isInLoginMode: boolean;
  program?: EnterpriseProgramsV1;
  thirdPartyOrganization?: ThirdPartyOrganizationsV1;
  isProgramAdmin?: boolean;
  isProgramMember?: boolean;
  isAuthenticatedUser?: boolean;
};

const useSsoCheck = ({
  router,
  userId,
  isInLoginMode,
  program,
  thirdPartyOrganization,
  isProgramAdmin,
  isProgramMember,
  isAuthenticatedUser,
}: SsoCheckProps) => {
  const prevStateRef = useRef({ isAuthenticatedUser, isInLoginMode });

  useEffect(() => {
    let { query } = router.location;

    if (isAuthenticatedUser) {
      const { itemId, attemptSSOLogin, productType } = query;

      query = omit(router.location.query, ['attemptSSOLogin']);

      if (itemId && attemptSSOLogin && productType === 'course') {
        query = {
          ...query,
          deepLinkItemId: itemId,
        };
      }
    }

    if (thirdPartyOrganization?.loginMethod === 'SAML') {
      router.replace({
        pathname: router.location.pathname,
        params: router.params,
        query: { ...query, authProvider: thirdPartyOrganization.slug },
        state: { curriculumId: program && program.metadata.curriculumId },
      });
    }

    if (userId && !(isProgramMember || isProgramAdmin)) {
      // Redirect to program home if the user is not a member or admin
      if (thirdPartyOrganization?.loginMethod === 'SAML') {
        query = { ...query, authProvider: thirdPartyOrganization.slug };
      }

      router.push({
        pathname: router.location.pathname,
        // @ts-expect-error TS2769 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        params: { programSlug: router.params.programSlug },
        query,
      });
    }

    if (isAuthenticatedUser && isInLoginMode) {
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: omit(router.location.query, ['authMode', 'attemptSSOLogin']),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { isAuthenticatedUser: prevIsAuthenticatedUser, isInLoginMode: prevIsInLoginMode } = prevStateRef.current;

    if (
      (isAuthenticatedUser !== prevIsAuthenticatedUser || isInLoginMode !== prevIsInLoginMode) &&
      isAuthenticatedUser &&
      isInLoginMode
    ) {
      router.push({
        pathname: router.location.pathname,
        params: router.params,
        query: omit(router.location.query, ['authMode', 'attemptSSOLogin']),
      });
    }

    prevStateRef.current = { isAuthenticatedUser, isInLoginMode };
  }, [isAuthenticatedUser, isInLoginMode, router]);
};

export default useSsoCheck;
